import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Interface } from './components/Interface.jsx';
import { TouchControls, useTouchMode } from './components/controls/TouchControls.jsx';
import { Comms } from './components/Comms.jsx';
import { InteractionPrompt } from './components/InteractionPrompt.jsx';
import { CREDITS, ERAS, STORY_CHAPTERS } from './game/story.js';
import { loadLookSettings, saveLookSettings } from './game/settings.js';
import { createInput, resetInput } from './game/input.js';
import { createAudio, haptic } from './game/audio.js';
import { loadProgress, saveProgress, clearProgress, PROGRESS_VERSION } from './game/progress.js';
import { getComms, ROUTE, ROUTE_OBJECTIVES, routeSession } from './game/storyAdapter.js';
import { musicScene } from './game/musicState.js';
import { requestLookLock } from './game/pointerLock.js';
import './styles/controls.css';

const Experience = lazy(() =>
  import('./components/Experience.jsx').then((module) => ({ default: module.Experience })),
);
const freshSession = () => routeSession();

export function App() {
  const [phase, setPhase] = useState('menu');
  const [chapterIndex, setChapterIndex] = useState(0);
  const [lookSettings, setLookSettings] = useState(loadLookSettings);
  const [paused, setPaused] = useState(false);
  const [contextLost, setContextLost] = useState(false);
  const [session, setSession] = useState(freshSession);
  const [progress, setProgress] = useState(freshSession);
  const checkpoint = useRef(loadProgress());
  const [saved, setSaved] = useState(checkpoint.current.checkpointId === ROUTE.checkpoint.id);
  const runtime = useRef(freshSession());
  const [hidden, setHidden] = useState(document.hidden);
  const [target, setTarget] = useState(null);
  const [message, setMessage] = useState('');
  const [queue, setQueue] = useState([]);
  const [landscapeHint, setLandscapeHint] = useState(true);
  const [frozen, setFrozen] = useState(false);
  const freezeTimer = useRef(null);
  const ignoreClickUntil = useRef(0);
  const input = useMemo(createInput, []);
  const audio = useMemo(createAudio, []);
  const [muted, setMuted] = useState(audio.muted);
  const touch = useTouchMode();
  const playing = phase === 'playing';
  const pause = useCallback(() => {
    setPaused(true);
    resetInput(input);
    document.exitPointerLock?.();
  }, [input]);
  const onLockChange = useCallback(
    (locked) => {
      if (!locked && playing && !touch) pause();
    },
    [playing, touch, pause],
  );
  const onContextChange = useCallback(
    (lost) => {
      setContextLost(lost);
      if (lost) pause();
    },
    [pause],
  );
  const updateLookSettings = useCallback(
    (patch) => setLookSettings((value) => saveLookSettings({ ...value, ...patch })),
    [],
  );
  const persist = useCallback((value) => {
    checkpoint.current = saveProgress({
      version: PROGRESS_VERSION,
      checkpointId: ROUTE.checkpoint.id,
      completed: false,
      route: {
        objectiveIndex: value.objectiveIndex,
        completed: value.completed,
        heard: value.heard,
      },
    });
    setSaved(true);
  }, []);
  const enterBeat = useCallback((value) => {
    const beat = ROUTE.beats[value.objectiveIndex];
    setQueue(beat ? getComms(beat.id, value.heard) : []);
  }, []);
  const onReady = useCallback(() => {
    enterBeat(runtime.current);
    persist(runtime.current);
  }, [enterBeat, persist]);
  const onDone = useCallback(() => setQueue((value) => value.slice(1)), []);
  const onPing = useCallback(
    (line) => {
      audio.cue('comms');
      if (!runtime.current.heard.includes(line.id)) {
        runtime.current.heard.push(line.id);
        persist(runtime.current);
      }
    },
    [audio, persist],
  );
  const resume = () => {
    if (contextLost) return;
    resetInput(input);
    audio.unlock();
    setPaused(false);
    if (!touch) requestLookLock();
  };
  const beginMission = (restore = false) => {
    if (!restore) clearProgress();
    const next = restore ? routeSession(checkpoint.current) : freshSession();
    runtime.current = next;
    resetInput(input);
    setSession({ ...next });
    setProgress({ ...next });
    setTarget(null);
    setMessage('');
    setQueue([]);
    setPaused(false);
    setFrozen(false);
    setPhase('playing');
    audio.unlock();
    if (!touch) requestLookLock();
  };
  const onProgress = useCallback(
    (value, advanced) => {
      value.heard = runtime.current.heard;
      runtime.current = value;
      setProgress(value);
      if (value.objectiveIndex < ROUTE_OBJECTIVES.length) persist(value);
      if (advanced) {
        audio.stinger('objective_complete');
        haptic(35);
        enterBeat(value);
      }
      if (value.objectiveIndex >= ROUTE_OBJECTIVES.length) {
        // A held stick can end over a button on the completion screen.
        ignoreClickUntil.current = performance.now() + 600;
        setPhase('complete');
        resetInput(input);
        document.exitPointerLock?.();
      }
    },
    [audio, enterBeat, persist, input],
  );
  const onAction = useCallback(
    (id, text) => {
      setMessage(text);
      audio.cue('interact');
      haptic();
      if (id === 'freeze' || id === 'rewind') audio.stinger(id);
      if (id === 'freeze') {
        setFrozen(true);
        clearTimeout(freezeTimer.current);
        freezeTimer.current = setTimeout(() => setFrozen(false), 3000);
      }
    },
    [audio],
  );

  useEffect(() => {
    const unlock = () => audio.unlock();
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      audio.dispose();
      clearTimeout(freezeTimer.current);
    };
  }, [audio]);
  useEffect(() => {
    audio.setPaused((playing && paused) || hidden);
    if (!playing || paused) resetInput(input);
  }, [audio, playing, paused, hidden, input]);
  useEffect(() => {
    const down = (event) => {
      if (!playing || /INPUT|TEXTAREA|SELECT/.test(event.target.tagName)) return;
      if (event.code === 'Escape' || event.code === 'KeyP') {
        if (!event.repeat) pause();
        return;
      }
      if (paused) return;
      if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.code))
        event.preventDefault();
      input.keys.add(event.code);
      const action = { Space: 'jump', KeyE: 'interact', KeyQ: 'freeze', KeyR: 'rewind' }[
        event.code
      ];
      if (action && !event.repeat) input.actions.add(action);
    };
    const up = (event) => input.keys.delete(event.code);
    const blur = () => {
      resetInput(input);
      if (playing) pause();
    };
    const visibility = () => {
      setHidden(document.hidden);
      if (document.hidden) blur();
      else audio.unlock();
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [input, playing, paused, pause, audio]);
  const chapter = STORY_CHAPTERS[chapterIndex];
  const era = ERAS[frozen ? Math.min(1, ERAS.length - 1) : 0];
  useEffect(() => {
    audio.setScene(musicScene(phase, chapter, era.id));
  }, [audio, phase, chapter, era.id]);
  const sceneState = useMemo(
    () => ({ phase, chapterIndex, tone: chapter?.tone ?? 'night', era: era.id, paused, frozen }),
    [phase, chapterIndex, chapter?.tone, era.id, paused, frozen],
  );
  return (
    <main
      className={`app phase-${phase}${touch ? ' is-touch' : ''}`}
      style={{ '--accent': era.accent }}
      onClickCapture={(event) => {
        if (event.detail && performance.now() < ignoreClickUntil.current) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
    >
      <Suspense fallback={<div className="experience-loading" aria-hidden="true" />}>
        <Experience
          sceneState={sceneState}
          lookSettings={lookSettings}
          input={input}
          session={session}
          paused={paused}
          touch={touch}
          completed={progress.completed}
          onReady={onReady}
          onProgress={onProgress}
          onTarget={setTarget}
          onAction={onAction}
          audio={audio}
          onLockChange={onLockChange}
          onContextChange={onContextChange}
        />
      </Suspense>
      <Interface
        phase={phase}
        chapter={chapter}
        chapterIndex={chapterIndex}
        objective={ROUTE_OBJECTIVES[progress.objectiveIndex]}
        objectiveIndex={progress.objectiveIndex}
        era={era}
        lookSettings={lookSettings}
        paused={paused}
        touch={touch}
        gauntlet={progress.inventory.includes('gauntlet')}
        saved={saved}
        contextLost={contextLost}
        onLookSettingsChange={updateLookSettings}
        onBeginStory={() => {
          setChapterIndex(0);
          setPhase('story');
        }}
        onNextChapter={() =>
          chapterIndex < STORY_CHAPTERS.length - 1
            ? setChapterIndex(chapterIndex + 1)
            : setPhase('briefing')
        }
        onPreviousChapter={() =>
          chapterIndex ? setChapterIndex(chapterIndex - 1) : setPhase('menu')
        }
        onSkipStory={() => setPhase('briefing')}
        onBeginMission={() => beginMission()}
        onContinue={() => beginMission(true)}
        onResume={resume}
        onReplay={() => {
          clearProgress();
          setSaved(false);
          setChapterIndex(0);
          setPhase('story');
        }}
      />
      {playing && (
        <>
          {!paused && touch && (
            <TouchControls input={input} gauntlet={progress.inventory.includes('gauntlet')} />
          )}
          {!paused && <InteractionPrompt target={target} message={message} touch={touch} />}
          <Comms queue={queue} paused={paused} onDone={onDone} onPing={onPing} />
          {!paused && (
            <button className="pause-button control-button" onClick={pause}>
              Pause
            </button>
          )}
          {touch && landscapeHint && !paused && (
            <button className="landscape-hint" onClick={() => setLandscapeHint(false)}>
              More room in landscape · dismiss
            </button>
          )}
        </>
      )}
      <button
        className="mute-button control-button"
        aria-label={muted ? 'Unmute audio' : 'Mute audio'}
        aria-pressed={muted}
        onClick={() => setMuted(audio.toggleMute())}
      >
        {muted ? 'Sound off' : 'Sound on'}
      </button>
      <span className="story-credit">{CREDITS.story}</span>
    </main>
  );
}
